import React from 'react';
import classNames from 'classnames';
import { dateTimeZone, getTimeZone, metersToMiles, roundNumber } from './converters';
import { showValueInPercent } from './financeUtils';
import { Nullable, Entity, VisiblePassengerOptions, PassengerInfo } from 'app-types';
import { PhoneExternalLink, VipBadge, JourneyNotes } from 'components';
import { ColumnProps } from 'components/antd/Table/Table';
import { isNil } from 'lodash-es';
import { getTextValue } from './utils';
import styles from './tableUtils.module.scss';
import { getPhoneCodeByCountryId } from 'utils/utils';
import { DictionaryCountry } from 'AppGS/api/dictionaries/dictionariesServiceTypes';
import { PendingDespatchBooking } from 'AppGS/api/despatch/despatchServiceTypes';

const getWrappedComponent = (getComponent: any, props: any) => {
	// tslint:disable-next-line
	return (text: any, record: any) =>
		getComponent ? getComponent(text, record, props) : undefined;
};
export function getColumnsWithRender<P>(
	columns: Array<ColumnProps<any>>,
	columnComponents: { [key: string]: any }
) {
	return function f(props: P): Array<ColumnProps<{}>> {
		const resultCols: Array<{}> = [];
		columns.forEach(item => {
			let getComponent: any = item.dataIndex ? getTextValue : undefined;
			if (columnComponents[item.key || '']) {
				getComponent = getWrappedComponent(columnComponents[item.key || ''], props);
			}
			resultCols.push({
				...item,
				render: getComponent,
			});
		});
		return resultCols;
	};
}

export function getPickupTime(date?: string) {
	if (!date) {
		return getTextValue(date);
	}
	return (
		<>
			<div className={styles.wordWrapHelper}>{dateTimeZone(date)}</div>
			<div>(UTC {getTimeZone(date)})</div>
		</>
	);
}

export function getVisiblePassengerOptions<
	P extends { passengersInfo?: PassengerInfo[]; allCountries: DictionaryCountry[] },
	R
>(visibleOptions: VisiblePassengerOptions) {
	return (bookingId: number, record: R, props: P) => {
		return getPassenger(bookingId, record, props, visibleOptions);
	};
}

export function getPassenger<
	P extends { passengersInfo?: PassengerInfo[]; allCountries: DictionaryCountry[] },
	R extends { numberOfPassengers?: number }
>(bookingId: number, record: R, props: P, visibleOptions: VisiblePassengerOptions = {}) {
	const { passengersInfo, allCountries } = props;
	const { numberOfPassengers } = record;
	const { showVip = false, showNotes = false } = visibleOptions;

	if (passengersInfo) {
		const currentPassenger = passengersInfo.filter(
			(item: any) => item.bookingId === bookingId
		)[0];
		const passengersNum = isNil(numberOfPassengers) ? 0 : numberOfPassengers - 1;

		if (currentPassenger && allCountries) {
			const phoneCode = getPhoneCodeByCountryId(
				allCountries,
				currentPassenger.phoneCodeCountryId
			);

			return (
				<>
					<div>
						{showVip && currentPassenger.vip && (
							<>
								<VipBadge />{' '}
							</>
						)}
						{currentPassenger.firstName}{' '}
						<span className={styles.wordWrapHelper}>
							{currentPassenger.lastName} {passengersNum > 0 && `(+${passengersNum})`}
						</span>
						{showNotes && currentPassenger.journeyNotes && (
							<JourneyNotes text={currentPassenger.journeyNotes} />
						)}
					</div>
					<PhoneExternalLink
						phoneCode={phoneCode}
						phoneNumber={currentPassenger.phoneNumber}
					/>
				</>
			);
		}
	}

	return undefined;
}

export function getJourneyInMile(meters: number) {
	return <div>{metersToMiles(meters, 'mi')}</div>;
}

export function getPriceWithCurrency(value: Nullable<number | string>, currencySymbol: string) {
	return (
		<div className={classNames({ [styles.emptyFinanceField]: isNil(value) })}>
			{currencySymbol} {isNil(value) ? getTextValue(value) : roundNumber(value)}
		</div>
	);
}

export function getFinanceValue(currencySymbol?: string) {
	return (value: Nullable<number>, record: any) =>
		getPriceWithCurrency(value, record[currencySymbol || 'customerCurrencySymbol']);
}

export const getDays = (value: Nullable<number>) => {
	if (isNil(value)) {
		return getTextValue(value);
	}
	let dayName = 'days';
	if (value === 1) {
		dayName = 'day';
	}
	return (
		<>
			{value} {dayName}
		</>
	);
};

export function getSelectValue<P>(selectPropName: string) {
	return (text: number, _record: any, props: P) => {
		const entityValue = props[selectPropName].find((item: Entity) => {
			return item.id === text;
		});

		return getTextValue(entityValue && entityValue.name);
	};
}

export function getPercentValue(value: Nullable<string | number>) {
	if (!isNil(value)) {
		return showValueInPercent(value);
	}
	return `% ${getTextValue(value)}`;
}

export const getTime = (text: string) => {
	const timePartials = text.split(':');

	return `${timePartials[0]}:${timePartials[1]}`;
};

export function getValueInMinutes(value: Nullable<string | number>) {
	if (!isNil(value)) {
		return `${value} min`;
	}
	return getTextValue(value);
}

export const getPickupAddress = (_: number, record: PendingDespatchBooking) => {
	return record.bookingAddresses[0];
};

export const getDropoffAddress = (_: number, record: PendingDespatchBooking) => {
	const { bookingAddresses } = record;
	return bookingAddresses[bookingAddresses.length - 1];
};

export const getCustomerWithNumber = (customer: string) => {
	const customerArray = customer.split(' ');
	const customerCompanyNumber = customerArray.pop();

	return (
		<>
			{customerArray.join(' ')}
			<br />
			{customerCompanyNumber}
		</>
	);
};

export function getJobIdHighlightedVip<
	P extends { passengersInfo?: PassengerInfo[]; showBookingDetails: (record: R) => void },
	R extends { bookingId: number }
>() {
	return (jobId: string, record: R, props: P) => {
		const { bookingId } = record;
		const { passengersInfo } = props;
		let vipCellClass = '';

		if (passengersInfo) {
			const currentPassenger = passengersInfo.filter(
				(item: any) => item.bookingId === bookingId
			)[0];

			vipCellClass = currentPassenger && currentPassenger.vip ? 'hasVIPPassenger' : '';
		}

		return {
			props: {
				className: vipCellClass,
			},
			children: <div onClick={() => props.showBookingDetails(record)}>{jobId}</div>,
		};
	};
}
